import type { Placement } from "@popperjs/core";
import { MutableRefObject, useCallback, useEffect, useRef, useState } from "react";
import ActionMenu, { type ActionMenuAction } from "../ActionMenu";
import IconButton from "../IconButton/index.jsx";
import StickyNoteAnchor from "../StickyTour/StickyNoteAnchor.js";
import { AnchorIds } from "../StickyTour/TourTypes";
import { HotDogDotsContainer } from "./styles.js";

export type { ActionMenuAction };

interface DotsActionMenuProps {
  actions: Record<string, ActionMenuAction>;
  position?: Placement;
  size?: number;
  iconSize?: number;
  smallDots?: boolean;
  portalRef?: MutableRefObject<HTMLElement | null>;
  className?: string;
  smartFill?: boolean;
  anchorId?: AnchorIds;
  ariaLabel?: string;
}

const DotsActionMenu = ({ actions, position, size, iconSize, smallDots = false, portalRef, className = "", smartFill = false, anchorId, ariaLabel = "Actions menu" }: DotsActionMenuProps) => {
  const [visible, setVisible] = useState(false);
  const triggerRef = useRef<HTMLDivElement>(null);
  const internalPortalRef = useRef<HTMLElement | null>(null);
  const mergedPortalRef = portalRef ?? internalPortalRef;
  const openMenuRef = useRef<(event: React.MouseEvent | React.KeyboardEvent) => void>(() => {});

  const visibleRef = useRef(false);
  visibleRef.current = visible;

  const handleButtonClick = useCallback((event: React.MouseEvent | React.KeyboardEvent) => {
    event.stopPropagation();

    if (visibleRef.current) {
      setVisible(false);
      return;
    }

    openMenuRef.current(event);
    setVisible(true);
  }, []);

  const handleMenuClose = useCallback(() => setVisible(false), []);

  useEffect(() => {
    if (!visible) {
      return;
    }

    function handleDocumentClick(event: MouseEvent | TouchEvent) {
      const target = event.target as Node;
      if (triggerRef.current?.contains(target)) {
        return;
      }
      if (mergedPortalRef.current?.contains(target)) {
        return;
      }
      setVisible(false);
    }

    document.addEventListener("pointerdown", handleDocumentClick);
    document.addEventListener("mousedown", handleDocumentClick);
    document.addEventListener("touchstart", handleDocumentClick);

    return () => {
      document.removeEventListener("pointerdown", handleDocumentClick);
      document.removeEventListener("mousedown", handleDocumentClick);
      document.removeEventListener("touchstart", handleDocumentClick);
    };
  }, [visible, mergedPortalRef]);

  return (
    <ActionMenu
      actions={actions}
      position={position}
      portalRef={mergedPortalRef}
      visible={visible}
      onClose={handleMenuClose}
    >
      {(openMenu: (event: React.MouseEvent | React.KeyboardEvent) => void) => {
        openMenuRef.current = openMenu;

        return (
        <HotDogDotsContainer ref={triggerRef}>
          {anchorId && <StickyNoteAnchor anchorId={anchorId} />}
          <div className="hotdog-dots">
            <IconButton
              smartFill={smartFill}
              onClick={handleButtonClick}
              src={smallDots ? "/assets/three-dots.svg" : "/assets/hotdog-menu.svg"}
              ariaLabel={ariaLabel}
              size={size}
              iconSize={iconSize}
              className={className}
              aria-haspopup="menu"
              aria-expanded={visible}
            />
          </div>
        </HotDogDotsContainer>
        );
      }}
    </ActionMenu>
  );
};

export default DotsActionMenu;
